import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Info, CheckCircle, XCircle, Search, Download, Filter } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
export function AdminLogs() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const logs = [{
    id: 1,
    level: 'error',
    message: 'Gagal login - Password salah',
    user: 'john@example.com',
    ip: '192.168.1.100',
    timestamp: '2025-01-15 14:30:25',
    details: 'Percobaan login ke-3 gagal'
  }, {
    id: 2,
    level: 'warning',
    message: 'Rate limit tercapai',
    user: 'jane@example.com',
    ip: '192.168.1.101',
    timestamp: '2025-01-15 14:25:10',
    details: 'Terlalu banyak request dalam 1 menit'
  }, {
    id: 3,
    level: 'info',
    message: 'Login berhasil',
    user: 'admin@atios.or.id',
    ip: '192.168.1.1',
    timestamp: '2025-01-15 14:20:00',
    details: 'Super Admin login'
  }, {
    id: 4,
    level: 'success',
    message: 'Backup database berhasil',
    user: 'system',
    ip: 'localhost',
    timestamp: '2025-01-15 14:00:00',
    details: 'Backup otomatis harian'
  }, {
    id: 5,
    level: 'error',
    message: 'API timeout',
    user: 'system',
    ip: 'api.atios.or.id',
    timestamp: '2025-01-15 13:45:30',
    details: 'Payment gateway tidak merespon'
  }];
  const filteredLogs = logs.filter(log => {
    const matchesTab = activeTab === 'all' || log.level === activeTab;
    const matchesSearch = log.message.toLowerCase().includes(searchQuery.toLowerCase()) || log.user.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });
  const stats = [{
    label: 'Error',
    value: logs.filter(l => l.level === 'error').length.toString(),
    icon: XCircle,
    color: 'text-error',
    bgColor: 'bg-error-light'
  }, {
    label: 'Warning',
    value: logs.filter(l => l.level === 'warning').length.toString(),
    icon: AlertTriangle,
    color: 'text-warning',
    bgColor: 'bg-warning-light'
  }, {
    label: 'Info',
    value: logs.filter(l => l.level === 'info').length.toString(),
    icon: Info,
    color: 'text-atios-primary',
    bgColor: 'bg-atios-light'
  }, {
    label: 'Success',
    value: logs.filter(l => l.level === 'success').length.toString(),
    icon: CheckCircle,
    color: 'text-success',
    bgColor: 'bg-success-light'
  }];
  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return <Badge variant="error" data-id="element-618">ERROR</Badge>;
      case 'warning':
        return <Badge variant="warning" data-id="element-619">WARNING</Badge>;
      case 'info':
        return <Badge variant="primary" data-id="element-620">INFO</Badge>;
      case 'success':
        return <Badge variant="success" data-id="element-621">SUCCESS</Badge>;
      default:
        return <Badge data-id="element-622">UNKNOWN</Badge>;
    }
  };
  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <XCircle className="w-5 h-5 text-error" data-id="element-623" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-warning" data-id="element-624" />;
      case 'info':
        return <Info className="w-5 h-5 text-atios-primary" data-id="element-625" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success" data-id="element-626" />;
      default:
        return <Info className="w-5 h-5 text-neutral-400" data-id="element-627" />;
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-628">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-629">
        {/* Header */}
        <div className="mb-8" data-id="element-630">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4" data-id="element-631">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-632" />
            Kembali ke Dasbor
          </Button>
          <div className="flex justify-between items-start" data-id="element-633">
            <div data-id="element-634">
              <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-635">
                Log Keamanan Sistem
              </h1>
              <p className="text-neutral-600" data-id="element-636">
                Pantau log keamanan dan aktivitas sistem
              </p>
            </div>
            <Button variant="secondary" data-id="element-637">
              <Download className="w-4 h-4 mr-2" data-id="element-638" />
              Export Log
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-639">
          {stats.map((stat, i) => <Card key={i} data-id="element-640">
              <CardContent className="p-6" data-id="element-641">
                <div className="flex items-center gap-4" data-id="element-642">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`} data-id="element-643">
                    <stat.icon className={`w-6 h-6 ${stat.color}`} data-id="element-644" />
                  </div>
                  <div data-id="element-645">
                    <p className="text-2xl font-semibold text-neutral-900" data-id="element-646">
                      {stat.value}
                    </p>
                    <p className="text-sm text-neutral-600" data-id="element-647">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-648">
          <div className="flex-1 relative" data-id="element-649">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" data-id="element-650" />
            <Input placeholder="Cari log..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-651" />
          </div>
          <Button variant="secondary" data-id="element-652">
            <Filter className="w-4 h-4 mr-2" data-id="element-653" />
            Filter
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full" data-id="element-654">
          <TabsList className="bg-neutral-100 border border-neutral-200" data-id="element-655">
            <TabsTrigger value="all" onClick={() => setActiveTab('all')} data-id="element-656">
              Semua
            </TabsTrigger>
            <TabsTrigger value="error" onClick={() => setActiveTab('error')} data-id="element-657">
              <XCircle className="w-4 h-4 mr-2" data-id="element-658" />
              Error
            </TabsTrigger>
            <TabsTrigger value="warning" onClick={() => setActiveTab('warning')} data-id="element-659">
              <AlertTriangle className="w-4 h-4 mr-2" data-id="element-660" />
              Warning
            </TabsTrigger>
            <TabsTrigger value="info" onClick={() => setActiveTab('info')} data-id="element-661">
              <Info className="w-4 h-4 mr-2" data-id="element-662" />
              Info
            </TabsTrigger>
            <TabsTrigger value="success" onClick={() => setActiveTab('success')} data-id="element-663">
              <CheckCircle className="w-4 h-4 mr-2" data-id="element-664" />
              Success
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6" data-id="element-665">
            <Card data-id="element-666">
              <CardContent className="p-0" data-id="element-667">
                <Table data-id="element-668">
                  <TableHeader data-id="element-669">
                    <TableRow data-id="element-670">
                      <TableHead className="w-[50px]" data-id="element-671"></TableHead>
                      <TableHead data-id="element-672">Level</TableHead>
                      <TableHead data-id="element-673">Pesan</TableHead>
                      <TableHead data-id="element-674">Pengguna</TableHead>
                      <TableHead data-id="element-675">IP Address</TableHead>
                      <TableHead data-id="element-676">Waktu</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-677">
                    {filteredLogs.map(log => <TableRow key={log.id} data-id="element-678">
                        <TableCell data-id="element-679">{getLevelIcon(log.level)}</TableCell>
                        <TableCell data-id="element-680">{getLevelBadge(log.level)}</TableCell>
                        <TableCell data-id="element-681">
                          <p className="font-medium text-neutral-900" data-id="element-682">
                            {log.message}
                          </p>
                          <p className="text-xs text-neutral-500 mt-1" data-id="element-683">
                            {log.details}
                          </p>
                        </TableCell>
                        <TableCell className="text-neutral-600" data-id="element-684">
                          {log.user}
                        </TableCell>
                        <TableCell className="text-neutral-600 font-mono text-xs" data-id="element-685">
                          {log.ip}
                        </TableCell>
                        <TableCell className="text-neutral-600 text-sm" data-id="element-686">
                          {new Date(log.timestamp).toLocaleString('id-ID', {
                        dateStyle: 'short',
                        timeStyle: 'medium'
                      })}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>;
}